<template>
  <div class="map">
    <a
      href="https://www.here.com/"
      target="_blank"
      rel="noopener"
    >
      <img
        class="here-logo"
        src="@/assets/here-logo.png"
        width="160"
        height="103"
        alt="Here"
      >
    </a>
  </div>
</template>

<script>
import {
  Map,
  TileLayer,
  LatLng,
  LatLngBounds
} from 'leaflet'
import makeTileLayerUrl, { defaultOptions } from '@/util/map/makeTileLayerUrl'

export default {
  name: 'LeafletMap',
  map: null,
  props: {
    layers: {
      type: Array,
      default: () => [new TileLayer(makeTileLayerUrl(), defaultOptions)]
    },
    center: {
      type: [Array, LatLng],
      default: () => [20, 0]
    },
    zoom: {
      type: Number,
      default: 3
    },
    fitBounds: {
      type: [Array, LatLngBounds],
      default: undefined
    },
    fitOptions: {
      type: Object,
      default: undefined
    },
    attributionControl: {
      type: Boolean,
      default: true
    },
    zoomControl: {
      type: Boolean,
      default: true
    },
    scrollWheelZoom: {
      type: Boolean,
      default: true
    },
    maxZoom: {
      type: Number,
      default: undefined
    }
  },
  watch: {
    layers(layers, oldLayers) {
      const { map } = this.$options
      if (!map) {
        return
      }

      const removed = oldLayers.filter(layer => layers.indexOf(layer) < 0)

      removed.forEach(layer => map.removeLayer(layer))
      layers.forEach((layer) => {
        if (oldLayers.indexOf(layer) < 0) {
          map.addLayer(layer)
        }
      })
    },
    center(center, oldCenter) {
      if (center.lat !== oldCenter.lat || center.lng !== oldCenter.lng) {
        this.$options.map.panTo(center)
      }
    },
    zoom(zoom, oldZoom) {
      if (zoom !== oldZoom) {
        this.$options.map.setZoom(zoom)
      }
    },
    fitBounds(bounds, oldBounds) {
      if (bounds && !bounds.equals(oldBounds)) {
        this.$options.map.fitBounds(bounds, this.fitOptions)
      }
    }
  },
  mounted() {
    this.$options.map = new Map(this.$el, {
      layers: this.layers,
      center: this.center,
      zoom: this.zoom,
      attributionControl: this.attributionControl,
      zoomControl: this.zoomControl,
      scrollWheelZoom: this.scrollWheelZoom,
      maxZoom: this.maxZoom,
      trackResize: false
    })

    const bounds = this.fitBounds
    if (bounds) {
      this.$options.map.fitBounds(bounds, this.fitOptions)
    }

    this.$options.resizeHandler = this.resize.bind(this)
    this.bindResize()
  },
  beforeDestroy() {
    if (this.$options.map) {
      this.$options.map.remove()
    }
    this.unbindResize()
  },
  activated() {
    this.bindResize()
  },
  deactivated() {
    this.$options.map.stop()
    this.unbindResize()
  },
  methods: {
    bindResize() {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', this.$options.resizeHandler, { passive: true })
      }
    },
    unbindResize() {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', this.$options.resizeHandler, { passive: true })
      }
    },
    resize() {
      const { map } = this.$options
      if (map) {
        map.invalidateSize(false)
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../../node_modules/leaflet/dist/leaflet.css";

  .here-logo {
    position: absolute;
    bottom: map-get($spacers, 3);
    left: map-get($spacers, 3);
    width: 2.5rem;
    height: auto;
    z-index: 1000;

    @include media-breakpoint-up($split-view-breakpoint) {
      width: 4rem;
    }
  }
</style>
